// ENTRY DETAIL (UI rebuild batch 2) — Astra's compact receipt for one posted entry: the direction above the amount, the
// state, then who, the reference, the account and the posted time; everything else behind "More" — the books' own
// sentence, the cost and the note, the postings (every account, signed), and the existing Reverse door (components/ReverseEntry, its
// reason chips, read-back, drawn_by_deal / confirm answers and one red button untouched; a repeat of a reversal the books
// already hold is answered "already reversed" by the route, so an uncertain one is never doubled). Open party is the
// receipt's one primary, as Astra draws it.
//
// THERE IS NO GET FOR ONE ENTRY (COVERAGE row 7, mechanisms 6.4). The receipt reads the row the list already holds, as a
// sheet over that list; a link to one entry is `/movements?q=<reference>`, which opens straight onto this receipt when
// the reference names exactly one loaded row.
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { DetailSheet, EmptyState, Fact, Pending } from '../kit';
import { Delta } from '../../Ledger';
import { CopyRef } from '../../CopyRef';
import { Pill } from '../../Pill';
import {
  MovementFigure, costWords, lineAccount, linesOf, railWords,
  type MovementLineRow,
} from '../../MovementTable';
import { ReverseEntry, canReverse, type ReversedOutcome } from '../../ReverseEntry';
import type { MovementPosting, TradeRow } from '../../../lib/api';
import { displayAsset, fmtAmount, fmtDay, fmtTime } from '../../../lib/format';
import { movementKindLabel, partyName } from '../../../lib/naming';
import { dealOf, stateWord } from './words';

export function EntrySheet({ m, entity, dealsById, onOpenDeal, onReversed, onClose, postings, notice }: {
  m: MovementLineRow;
  /** Under More: the entry's postings (EntryPostings), or the line that says they are on their way / not read. */
  postings?: ReactNode;
  /** One quiet line at the top: the entry's own read failed, and this is the row the list had. */
  notice?: ReactNode;
  /** The desk the list was read for — a party link's fallback desk. */
  entity: string;
  dealsById: Map<string, TradeRow>;
  onOpenDeal: (dealId: string) => void;
  onReversed: (o: ReversedOutcome) => void;
  onClose: () => void;
}) {
  const lines = linesOf(m);
  const state = stateWord(m);
  const deal = dealOf(m);
  const partyHref = (p: { slug: string; entity?: string }) => `/parties/${encodeURIComponent(p.slug)}?entity=${p.entity ?? entity}`;
  const posted = m.at ? `${fmtDay(m.at, true)} · ${fmtTime(m.at)} Dubai` : '—';
  return (
    <DetailSheet
      title="Entry detail"
      onClose={onClose}
      footer={m.party ? <Link to={partyHref(m.party)} className="btn-primary w-full px-4 text-body lg:ml-auto lg:flex lg:w-fit">Open party</Link> : undefined}
    >
      {notice && <div className="mb-3">{notice}</div>}
      {/* THE AMOUNT under its direction (the Books' word for it), one line per currency the entry moved, and the state. */}
      <div className="border-b border-stroke-soft pb-4">
        <div className="text-label font-medium uppercase tracking-wider text-tg-hint">{movementKindLabel(m.kind)}</div>
        <div className="mt-2 space-y-1 font-mono tabular text-figure font-semibold">
          {lines.map((l) => <div key={l.key} className="break-words"><MovementFigure line={l} code /></div>)}
        </div>
        <Pill tone={m.state === 'reversal' ? 'warn' : m.state === 'live' ? 'good' : 'neutral'} className="mt-3">{state ?? 'Posted'}</Pill>
      </div>

      <div className="mt-2">
        {m.party && (
          <Fact label={m.counterparty ? 'From party' : 'Party'}>
            <Link to={partyHref(m.party)} className="btn-link">{partyName(m.party)}</Link>
          </Fact>
        )}
        {m.counterparty && (
          <Fact label="To party">
            <Link to={partyHref(m.counterparty)} className="btn-link">{partyName(m.counterparty)}</Link>
          </Fact>
        )}
        <Fact label="Reference">
          {m.reference ? <CopyRef value={m.reference} className="font-mono" /> : '—'}
        </Fact>
        {/* The originating deal, one quiet link: what was agreed is there (and Void, for a trade's own leg). */}
        {deal && (
          <Fact label="Deal">
            {dealsById.has(deal)
              ? <button type="button" className="btn-link" onClick={() => onOpenDeal(deal)}>Open deal</button>
              : <span className="font-mono">{deal}</span>}
          </Fact>
        )}
        {m.kind === 'rail_move' ? (
          <>
            {m.rail && <Fact label="From account">{railWords(m.rail)}</Fact>}
            {m.counter_rail && <Fact label="To account">{railWords(m.counter_rail)}</Fact>}
          </>
        ) : (
          lines.map((l) => {
            const a = lineAccount(l);
            return a ? <Fact key={l.key} label={lines.length > 1 ? `Account · ${displayAsset(l.asset)}` : 'Account'}>{railWords(a)}</Fact> : null;
          })
        )}
        <Fact label="Posted time">{posted}</Fact>
      </div>

      {/* MORE: the books' sentence, the cost and the note, the raw legs, the Reverse door — opened only when wanted. */}
      <details className="mt-4 border-t border-stroke-soft">
        <summary className="flex min-h-[44px] cursor-pointer items-center text-body lg:min-h-[40px]">More</summary>
        <div className="space-y-3 pb-2 text-body">
          <p className="break-words text-tg-hint">{m.words}</p>
          {(m.cost || m.notes) && (
            <div>
              {m.cost && (
                <Fact label="Cost">
                  <span className="amt">{fmtAmount(m.cost.amount, m.asset)}</span> {displayAsset(m.asset)} · {costWords(m.cost.category)}
                </Fact>
              )}
              {m.notes && <Fact label="Note">{m.notes}</Fact>}
            </div>
          )}
          {/* THE POSTINGS (GET /api/movements/:txn): every account the entry touched, signed, + into the account. */}
          {postings}
          {/* The door as today: a live money entry only (canReverse); a trade's own leg has none — Void is in its deal. */}
          {canReverse(m) && <ReverseEntry entry={m} onReversed={onReversed} />}
        </div>
      </details>
    </DetailSheet>
  );
}

/** THE POSTINGS: one line per account — its name (the account itself where the books give none: P&L, clearing, the
 *  world) — with the signed figure and the asset. */
export function EntryPostings({ postings }: { postings: MovementPosting[] }) {
  if (postings.length === 0) return null;
  return (
    <ul>
      {postings.map((p, i) => (
        <li key={`${p.account}|${p.asset}|${i}`} className="flex items-baseline justify-between gap-3 border-t border-stroke-soft py-2">
          <span className={`min-w-0 break-words ${p.name ? '' : 'font-mono text-meta text-tg-hint'}`}>{p.name ?? p.account}</span>
          <span className="shrink-0"><Delta value={Number(p.net)} asset={p.asset} code /></span>
        </li>
      ))}
    </ul>
  );
}

/** The receipt when there is no row to draw: the read on its way, the read failed, the books have no such entry, or an
 *  entry the list does not show (`movement: null`) — drawn from its postings alone, with no Reverse door. */
export function EntryShell({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  return <DetailSheet title="Entry detail" onClose={onClose}>{children}</DetailSheet>;
}
export const EntryWaiting = ({ onClose }: { onClose: () => void }) => <EntryShell onClose={onClose}><Pending /></EntryShell>;
export const EntryMissing = ({ onClose }: { onClose: () => void }) => (
  <EntryShell onClose={onClose}><EmptyState>This entry isn&rsquo;t in the books.</EmptyState></EntryShell>
);
